<template lang="pug">
div(
    :id="id"
    ref="input"
    :class="{ dense: dense, active: isFocused || animationOn }"
    class="tl-input-dropdown"
    data-form-validator
)
    div(
        :class="{ 'animation-shake': error }"
        class="input-wrapper"
    )
        transition(
            name="dropdown-content"
            @before-enter="animationOn = true"
            @after-leave="animationOn = false"
        )
            div(
                v-if="isFocused"
                ref="dropdownWrapper"
                class="dropdown-wrapper"
                :style="dropdownStyles"
            )
                div(
                    class="dropdown-content"
                    :style="{ height: dropdownContentHeight ? `${dropdownContentHeight}px` : '' }"
                )
                    label(
                        v-if="searchable"
                        class="dropdown-item dropdown-search search-input-wrapper"
                    )
                        div(
                            class="label-input-wrapper"
                        )
                            div
                                input(
                                    ref="searchableInput"
                                    v-model="query"
                                    placeholder="Search"
                                )
                    div(
                        v-for="(item, index) in itemsFilteredList"
                        :key="index"
                        v-ripple="{ color: 'gray' }"
                        class="dropdown-item"
                        :class="{ selected: checkIfSelected(item.value)}"
                        @click="setItem(item)"
                    )
                        slot(
                            name="item"
                            :item="item"
                        )
                            span(class="text-4") {{ item.label }}

        div(
            :class="{ error: error, focus: isFocused, smallLabel: (isFocused || !emptyModelState) && !!label, disabled: disabled, opaq: opaq, 'with-slot': !!(slots && slots.default), filled: emptyModelState }"
            class="label-input-wrapper full-width"
            tabindex="0"
            @click="handleDropdownOpen"
        )
            slot(
                :model-state="modelState"
                :model-text="modelText"
                :is-focused="isFocused"
            )
                div
                    tl-position(
                        class="label fill-height"
                        x="stretch"
                        y="center"
                    )
                        div  {{ label }}

                    transition(
                        name="fade-1"
                    )
                        div(
                            v-if="modelState"
                            :key="modelState"
                            class="value"
                        )
                            span(class="text-4 text-line-clamp line-clamp-1") {{ modelText }}

            div(
                v-if="resetValue && !emptyModelState"
                class="input-dropdown-clear-value"
                @click.stop="handleResetValue"
            )
                i(class="pi pi-times")

            div(class="arrow")
                i(class="pi pi-chevron-down")

        tl-input-error-text(
            :error="error"
        )
</template>

<script setup>
import { computed, onMounted, ref, watch, inject, nextTick, useSlots, onUnmounted } from 'vue'
import uniqid from 'uniqid'
import useRules from './validator/rules'
import { useEventBus } from '@vueuse/core'
import { getInjects } from '@/components/$tl/utils/injects'
import equals from 'fast-deep-equal/es6'
import { useFloating, autoPlacement, size } from '@floating-ui/vue'

const MAX_HEIGHT = 400

const props = defineProps({
    modelValue: [Array, Object, String, Number, undefined],
    error: [String, Boolean],
    disabled: Boolean,
    items: Array,
    label: String,
    opaq: Boolean,
    rules: Array,
    dense: Boolean,
    itemText: Function,
    itemValue: Function,
    searchable: Boolean,
    multiple: Boolean,
    allOption: [String, Object],
    dropdownWidth: Number,
    resetValue: Boolean,
})

const emit = defineEmits(['resetError', 'update:modelValue', 'validated'])

const { validateLogic } = useRules()
const injects = getInjects(props, inject)

const id = ref(uniqid())
const input = ref(null)
const dropdownWrapper = ref(null)
const modelValue = computed(() => props.modelValue ?? injects.modelValue?.value)
const modelState = ref(modelValue.value)
const internalError = ref(null)
const isFocused = ref(false)
const query = ref('')
const searchableInput = ref(null)
const dropdownContentHeight = ref(null)
const dropdownWidthVal = ref(null)
// true when animation is on to keep z-index till the end of transition
const animationOn = ref(false)

const slots = useSlots()

const { floatingStyles, update } = useFloating(input, dropdownWrapper, {
    placement: 'bottom-start',
    transform: false,
    middleware: [
        autoPlacement({
            autoAlignment: false,
            allowedPlacements: ['top-start', 'top-end', 'top', 'bottom', 'bottom-start', 'bottom-end'],
        }),
        size({
            apply({ availableHeight: _availableHeight, availableWidth: _availableWidth }) {
                const realHeightOfDropdownContent = dropdownWrapper.value?.children?.[0]?.clientHeight || 9999 // dummy value
                dropdownContentHeight.value = Math.min(_availableHeight, realHeightOfDropdownContent, MAX_HEIGHT)
                dropdownWidthVal.value = _availableWidth
            },
        }),
    ],
})

const dropdownStyles = computed(() => {
    // default width of dropdown should be 100%
    const width = dropdownWidthVal.value && props.dropdownWidth
        ?`${Math.min(dropdownWidthVal.value, props.dropdownWidth)}px`
        : '100%'
    return {
        ...floatingStyles.value,
        width,
    }
})

const error = computed(() => props.error ? props.error : internalError.value)

const allOption = computed(() => normalizedItem(props.allOption))

const items = computed(() => {
    const _items = injects.items?.value
        ? injects.items.value
        : props.items

    const result = _items.map(item => normalizedItem(item))

    if (props.allOption) {
        result.splice(0, 0, { ...allOption.value, allOption: true })
    }

    return result
})

const normalizedItem = item => ({
    label: props.itemText ? props.itemText(item) : item,
    value: props.itemValue ? props.itemValue(item) : item,
})

const itemsFilteredList = computed(() => {
    const _items = [...items.value]

    if (!props.searchable) {
        return _items
    }

    const queryLC = query.value.toLowerCase()
    return _items.filter(item => {
        if (queryLC && item.allOption) {
            return false
        }

        return item.label.toLowerCase().includes(queryLC)
    })
})

const opaq = computed(() => props.opaq ?? injects.opaq?.value)
const disabled = computed(() => props.disabled ?? injects.disabled?.value)
const label = computed(() => props.label ?? injects.label?.value)
// const rules = computed(() => props.rules ?? injects.rules?.value)
const dense = computed(() => props.dense ?? injects.dense?.value)
const modelText = computed(() => {
    const model = (props.multiple
        ? modelState.value
        : [modelState.value]).map(i => getItemByValue(i))

    return model.map(item => {
        return item?.label
    }).join(', ')
})

const emptyModelState = computed(() => {
    return Array.isArray(modelState.value)
        ? !modelState.value.length
        : !modelState.value
})

const getItemByValue = (val) => {
    const index = items.value.findIndex(item => equals(item.value, val))
    return index > -1 ? items.value[index] : undefined
}

const checkIfSelected = (val) => {
    if (props.multiple) {
        return modelState.value.includes(val)
    }

    return equals(modelState.value, val)
}

const handleMultiselect = (item) => {
    if (props.allOption) {
        const allOptionIndex = modelState.value.findIndex(a => a === allOption.value.value)
        if (allOptionIndex > -1) {
            modelState.value.splice(allOptionIndex, 1)
        }
    }

    const indexOfItem = modelState.value.findIndex((el) => el === item.value)
    if (indexOfItem > -1) {
        modelState.value.splice(indexOfItem, 1)
    } else {
        modelState.value.push(item.value)
    }
}

const setItem = (item) => {
    if (item.allOption) {
        modelState.value = props.multiple
            ? [item.value]
            : item.value
        closeDropdown()
        return
    }

    if (props.multiple) {
        handleMultiselect(item)
        return
    }
    modelState.value = item.value
    closeDropdown()
}

const closeDropdown = async () => {
    isFocused.value = false
    query.value = ''
}

const resetError = () => {
    internalError.value = null
    emit('resetError')
}

const handleDropdownOpen = () => {
    if (props.disabled) return
    isFocused.value = !isFocused.value
}

const detectClickOutside = (e) => {
    const clickInsideComponent = input.value?.contains(e.target)
    if (!clickInsideComponent) {
        closeDropdown()
    }
}

const handleResetValue = () => {
    if (props.multiple) {
        modelState.value = []
    } else {
        modelState.value = ''
    }
}

watch(modelValue, (val) => {
    modelState.value = val
})

watch(isFocused, (val) => {
    if (val) {
        window.addEventListener('mouseup', detectClickOutside)
        update()
        nextTick(() => {
            if (searchableInput.value) {
                searchableInput.value?.focus()
            }
        })
    } else {
        dropdownContentHeight.value = null
        window.removeEventListener('mouseup', detectClickOutside)
    }
})

onUnmounted(() => {
    window.removeEventListener('mouseup', detectClickOutside)
})

watch(modelState, (val) => {
    if (val) {
        resetError()
    }
    emit('update:modelValue', val)
}, { deep: true })

onMounted(() => {
    id.value = uniqid()
    useEventBus(`validate-${id.value}`).on(() => {
        validateLogic(props, modelState, input, internalError, emit)
    })
    useEventBus(`clear-error-${id.value}`).on(() => {
        resetError()
    })
})
</script>

<style lang="sass">

.tl-input-dropdown
    --p-icon-size: 10px

    overflow: visible
    position: relative
    z-index: 4
    &.dense
        .dropdown-content
            min-height: 45px
            .dropdown-item
                min-height: 45px !important
                padding: 0 var(--padding-4)
            .search-input-wrapper .label-input-wrapper
                padding-left: 0
                padding-right: 0
    &.active
        z-index: 100
    .dropdown-wrapper
        position: absolute
        top: 100%
        left: 0px
        z-index: 100
        width: 100%
    .dropdown-content
        min-height: 60px
        max-height: 400px
        background-color: #fff
        box-shadow: var(--elevation-20)
        border-radius: var(--border-radius)
        // transform: scale(1.01)
        overflow-x: hidden
        overflow-y: auto
        &.opaq
            box-shadow: none
            background-color: var(--color-gray-tint-medium)
        .dropdown-item
            min-height: 60px
            border-bottom: 1px solid rgba(0,0,0,0.05)
            padding: var(--padding-4)
            cursor: pointer
            transition: var(--transition-default)
            display: flex
            align-items: center
            &:last-child
                border-bottom: none
            &:hover,
            &.selected
                background-color: var(--color-tint-extralight)

            &.dropdown-search
                padding: 0
                .label-input-wrapper
                    padding: calc(var(--padding-4) - 4px)
                    border-radius: var(--border-radius) var(--border-radius) 0 0

        .search-input-wrapper
            padding: 0
            .label-input-wrapper
                border: none

    .label-input-wrapper
        cursor: pointer
        position: relative
        padding-right: 30px
        color: var(--p-select-color)

        .arrow
            position: absolute
            right: 2px
            top: 0px
            width: 25px
            height: 100%
            display: flex
            justify-content: flex-start
            align-items: center
            color: var(--p-select-clear-icon-color)
            .pi
                font-size: 13px

        .value
            padding-top: 0px
            padding-right: 35px
        &.smallLabel
            .value
                padding-top: 10px

        &.with-slot
            min-height: 45px
            height: auto
        .input-dropdown-clear-value
            position: absolute
            right: 28px
            top: 0
            height: 100%
            width: 25px
            display: flex
            justify-content: flex-start
            align-items: center
            transition: opacity .2s ease-in-out
            color: var(--p-select-clear-icon-color)
            .pi
                font-size: 13px

.dropdown-content-enter-from,
.dropdown-content-leave-to
    opacity: 0
.dropdown-content-enter-to,
.dropdown-content-leave-from
    opacity: 1

.dropdown-content-enter-active,
.dropdown-content-leave-active
    transition: opacity .2s ease-in-out

</style>
